// src/components/common/ConversationList.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useRedux';
import { getConversationsWithUnreadCounts, Conversation } from '../../services/chatService';
import { formatDistanceToNow } from 'date-fns';
import { useUI } from '../../context/UIContext';
import { useConversationTypingSocket } from '../../hooks/useConversationTypingSocket';
import { TYPING_PREVIEW } from '../../hooks/useTypingEmitter';
import { getToken as getSessionToken } from '../../utils/sessionManager';
import '../../styles/ConversationList.css';

interface ConversationListProps {
  onSelectConversation?: (conversationId: string) => void;
  onClose?: () => void;
}

const ConversationList: React.FC<ConversationListProps> = ({ 
  onSelectConversation,
  onClose 
}) => {
  const navigate = useNavigate();
  const { user, token: reduxToken } = useAppSelector(state => state.auth);
  const { showSnackbar } = useUI();
  
  const [conversations, setConversations] = useState<(Conversation & { unreadCount?: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const userId = user?.id || (user as any)?._id;
  const userType = user?.userType || 'client';
  // Canonical auth source: in-memory Redux/SessionManager token, not localStorage.
  const token = reduxToken || getSessionToken();
  const conversationIds = conversations.map((conversation) => conversation._id);
  const handleConversationUpdated = useCallback(
    (update: {
      conversationId: string;
      lastMessage: { content: string; sender: string; createdAt: string };
    }) => {
      setConversations((prev) =>
        prev.map((conversation) => {
          if (conversation._id !== update.conversationId) {
            return conversation;
          }

          if (!conversation.lastMessage) {
            return {
              ...conversation,
              updatedAt: new Date().toISOString(),
            };
          }

          return {
            ...conversation,
            lastMessage: {
              ...conversation.lastMessage,
              content: update.lastMessage.content,
              sender: update.lastMessage.sender,
              createdAt: update.lastMessage.createdAt,
            },
            updatedAt: new Date().toISOString(),
          };
        })
      );
    },
    []
  );
  const typingByConversation = useConversationTypingSocket(
    token,
    userId,
    conversationIds,
    handleConversationUpdated
  );

  const fetchConversations = useCallback(async (showRefreshing = false) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const convos = await getConversationsWithUnreadCounts();
      setConversations(convos);
    } catch (error: any) {
      console.error('Error fetching conversations:', error);
      showSnackbar('Failed to load conversations', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId, showSnackbar]);

  useEffect(() => {
    fetchConversations();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchConversations(true);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchConversations]);

  const handleConversationClick = (conversationId: string) => {
    if (onSelectConversation) {
      onSelectConversation(conversationId);
    } else {
      navigate(`/${userType}/chat/${conversationId}`);
    }
    if (onClose) {
      onClose();
    }
  };

  const getOtherParticipant = (conversation: Conversation) => {
    if (!conversation.participants || conversation.participants.length === 0) {
      return null;
    }

    const otherParticipant = conversation.participants.find((p: any) => {
      const id = typeof p === 'string' ? p : (p?._id || p);
      return id !== userId;
    });

    if (!otherParticipant) return null;

    if (typeof otherParticipant === 'object' && otherParticipant !== null) {
      const participant = otherParticipant as any;
      return {
        id: participant._id || participant.id || participant,
        name: participant.name || 'Unknown User',
        photo: participant.profilePhoto?.url || null,
        userType: participant.userType || null,
      };
    }

    return {
      id: otherParticipant as string,
      name: 'Unknown User',
      photo: null,
      userType: null,
    };
  };

  const getConversationTitle = (conversation: Conversation) => {
    if (conversation.type === 'support') {
      return 'Support Chat';
    }
    if (conversation.type === 'appointment') {
      return 'Appointment Chat';
    }
    const otherParticipant = getOtherParticipant(conversation);
    return otherParticipant?.name || 'Chat';
  };

  const formatLastMessageTime = (updatedAt: string) => {
    try {
      return formatDistanceToNow(new Date(updatedAt), { addSuffix: true });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="conversation-list">
        <div className="conversation-list__loading">
          <div className="spinner"></div>
          <p>Loading conversations...</p>
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="conversation-list">
        <div className="conversation-list__empty">
          <p>No conversations yet.</p>
          <p className="conversation-list__empty-hint">Start a chat from an appointment or contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      <div className="conversation-list__header">
        <button 
          className="conversation-list__refresh"
          onClick={() => fetchConversations(true)}
          disabled={refreshing}
          title="Refresh conversations"
        >
          {refreshing ? (
            <div className="spinner-small"></div>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
          )}
        </button>
      </div>

      <div className="conversation-list__items">
        {conversations.map((conversation) => {
          const otherParticipant = getOtherParticipant(conversation);
          const unreadCount = conversation.unreadCount || 0;
          const isUnread = unreadCount > 0;
          const isTyping = !!typingByConversation[conversation._id];
          const previewText = isTyping
            ? TYPING_PREVIEW
            : conversation.lastMessage?.content || 'No messages yet';

          return (
            <div
              key={conversation._id}
              className={`conversation-item ${isUnread ? 'conversation-item--unread' : ''}`}
              onClick={() => handleConversationClick(conversation._id)}
            >
              <div className="conversation-item__avatar">
                {otherParticipant?.photo ? (
                  <img src={otherParticipant.photo} alt={otherParticipant.name} />
                ) : (
                  <div className="conversation-item__avatar-placeholder">
                    {otherParticipant?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                {isUnread && (
                  <div className="conversation-item__unread-indicator"></div>
                )}
              </div>

              <div className="conversation-item__content">
                <div className="conversation-item__header">
                  <h3 className="conversation-item__title">
                    {getConversationTitle(conversation)}
                  </h3>
                  <span className="conversation-item__time">
                    {formatLastMessageTime(conversation.updatedAt)}
                  </span>
                </div>

                <p
                  className={`conversation-item__preview${
                    isTyping ? ' conversation-item__preview--typing' : ''
                  }`}
                >
                  {previewText}
                </p>

                <div className="conversation-item__footer">
                  <span className="conversation-item__type">
                    {conversation.type === 'support' ? 'Support' : 
                     conversation.type === 'appointment' ? 'Appointment' : 
                     'Chat'}
                  </span>
                  {isUnread && (
                    <span className="conversation-item__unread-badge">
                      {unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConversationList;

